import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { NotifIcon, notifLink } from "@/lib/notifications";

// Browser notifications, where supported and permitted. Native push plugs in server-side (push.py).
const showBrowserNotifications = (fresh) => {
  if (!fresh.length || !("Notification" in window) || Notification.permission !== "granted") return;
  fresh.slice(0, 3).forEach((n) => {
    try {
      const bn = new Notification(n.title, { body: n.body, tag: n.id });
      bn.onclick = () => { window.focus(); const l = notifLink(n); if (l) window.location.assign(l); };
    } catch { /* some mobile browsers only allow service-worker notifications */ }
  });
};

const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const seen = React.useRef(null);
  const load = async () => {
    try {
      const r = await api.get("/notifications");
      if (seen.current) showBrowserNotifications(r.data.filter((n) => !n.read && !seen.current.has(n.id)));
      seen.current = new Set(r.data.map((n) => n.id));
      setItems(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);

  const unread = items.filter(n => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems(items.map(n => ({ ...n, read: true })));
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v && unread > 0) markAll(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
          <Bell className="w-4 h-4"/>
          {unread > 0 && <span data-testid="notif-unread-count" className="absolute top-1 right-1 w-4 h-4 text-[10px] rounded-full bg-amber-500 text-white grid place-items-center font-bold">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold">Notifications</div>
          {items.length > 0 && <button onClick={markAll} className="text-xs text-slate-500 hover:text-slate-900">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">You're all caught up</div>}
          {items.slice(0, 8).map((n) => (
            <Link
              key={n.id}
              to={notifLink(n) || "/notifications"}
              onClick={() => setOpen(false)}
              className={`block px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${!n.read ? "bg-amber-50/60" : ""}`}
              data-testid={`notif-${n.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="pt-0.5 text-amber-700"><NotifIcon kind={n.kind}/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <div className="text-xs text-slate-500 truncate">{n.body}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/notifications" onClick={() => setOpen(false)} data-testid="notif-view-all"
              className="block text-center text-sm font-medium py-2.5 hover:bg-slate-50">View all notifications</Link>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
